import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  StatusChip,
} from "../../../../components/ui";
import { LeadTableSummary, SummaryType, SummaryTypeText } from "../shared/Constants";
import FormControl from "@mui/material/FormControl/FormControl";
import Select from "@mui/material/Select/Select";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import { HelpersService } from "../../../../services/Helpers";
import { LeadViewModel, UserShortResponse, Category } from "../../../../api";

const TABLE_COLUMNS = [
  { id: "leadName", label: "Lead name", sortable: false },
  { id: "category", label: "Category", sortable: false },
  { id: "termination", label: "Termination", sortable: false },
  { id: "summaryType", label: "Summary type", sortable: false },
];

interface ShareLeadsTableProps {
  leads: LeadTableSummary[];
  onPreviewSelect: Function;
  onPreviewTypeChange: Function;
  attorneyIds?: number[];
  attorneys?: UserShortResponse[];
  filterEligibleOnly?: boolean;
}

export default function ShareLeadsTable(props: ShareLeadsTableProps) {
  const maxMonths = HelpersService.getRequiredTerminationAgeMonths(
    props.attorneyIds ?? [],
    props.attorneys ?? []
  );

  const isEligible = (lead: LeadViewModel) => {
    if (!maxMonths) return true;
    if (lead.category !== Category.EMPLOYMENT && lead.category !== Category.CLASS_ACTION) return true;
    return HelpersService.isLeadWithinTerminationAge(lead, maxMonths);
  };

  const rows = (props.filterEligibleOnly
    ? props.leads.filter((row) => isEligible(row.lead))
    : props.leads
  ).map((row, index) => ({ row, index: props.leads.indexOf(row) }));

  return (
    <AppTable
      columns={TABLE_COLUMNS}
      showRowNumber={false}
      size="medium"
      trailingHeadCells={
        <AppTableHeadCell align="left" className="ps-5">
          Actions
        </AppTableHeadCell>
      }
      extraColumnCount={1}
      isEmpty={rows.length === 0}
      emptyTitle="No leads match the termination date filter for selected attorneys."
    >
      {rows.map(({ row, index }) => {
        const eligible = isEligible(row.lead);
        const termDate = row.lead.employmentDetails?.terminationDate;

        return (
          <AppTableRow hover tabIndex={-1} key={row.lead.id}>
            <AppTableCell align="left">
              {row.lead.firstName} {row.lead.lastName}
            </AppTableCell>
            <AppTableCell align="left">
              {HelpersService.camelToString(row.lead.category)}
            </AppTableCell>
            <AppTableCell align="left">
              {HelpersService.formatTerminationDate(termDate)}
              {maxMonths && (
                <StatusChip
                  size="small"
                  className="ms-2"
                  tone={eligible ? "success" : "warning"}
                  label={eligible ? "Eligible" : `>${maxMonths} mo`}
                />
              )}
            </AppTableCell>
            <AppTableCell align="left">
              <FormControl required variant="outlined" sx={{ m: 1, minWidth: 120 }}>
                <Select
                  value={row.summaryType}
                  onChange={(e) => props.onPreviewTypeChange(index, e.target.value)}
                >
                  {Object.keys(SummaryType).map((key) => (
                    <MenuItem key={key} value={key}>
                      {SummaryTypeText[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </AppTableCell>
            <AppTableCell align="left">
              <AppButton
                variant="text"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  props.onPreviewSelect(index);
                }}
              >
                Preview
              </AppButton>
            </AppTableCell>
          </AppTableRow>
        );
      })}
    </AppTable>
  );
}
